/* globals $ */
import { registerClass } from 'libs/ComponentLoader'
import inViewport from 'in-viewport'

import { formatMoney, formatImageUrl } from '_global/js/helpers'
import { getCustomerDiscount } from 'libs/DisplayDiscount'

import './styles.scss'

class ProductCarousel {
  constructor (element) {
    this.$el = $(element)

    this.slickObject = null
    this.isFetching = false
    this.carouselParams = {
      dataPromise: null,
      initSelf: (this.$el.attr('data-init-self') === 'true')
    }

    this.forceCount = parseInt(this.$el.attr('data-force-count'))

    this.$carousel = this.$el.find('.productCarousel-carousel')
    this.$cardTemplate = $(this.$el.find('.productCarousel-cardTemplate').html())

    if (this.carouselParams.initSelf) {
      this.initCarousel()
    }
  }

  initCarousel (params) {
    this.carouselParams = Object.assign(this.carouselParams, params)

    // fill out at least enough placeholders for desktop
    const totalInitialProducts = this.$carousel.find('.productCard').length
    const totalInitial = this.forceCount > 0 ? this.forceCount : 5
    if (totalInitialProducts < totalInitial) {
      for (let i = 0, len = (totalInitial - totalInitialProducts); i < len; i++) {
        this.$carousel.append(this.getPlaceholder())
      }
    }

    this.$carousel.on('init', (event, slick) => {
      // Please keep this console log, but comment it out when merging into master
      // The slick object is useful, but not really documented. Uncomment to view it in the console.
      // console.log(slick)

      this.slickObject = slick

      // Unfortunately slick can't really be fully used until the next js frame,
      // so we'll add a 0ms timeout before doing anything
      setTimeout(() => {
        if (this.checkNeedData()) {
          inViewport(this.$el[0], () => {
            this.fetchData()
          })
        }
      }, 0)
    })

    this.$carousel.on('afterChange', () => {
      if (this.checkNeedData()) {
        this.fetchData()
      }
    })

    if (this.forceCount <= 0) {
      const defaultSlickParams = {
        dots: false,
        arrows: true,
        rows: 0, // https://github.com/kenwheeler/slick/issues/3149
        infinite: true,
        slidesToShow: 5,
        slidesToScroll: 5,
        responsive: [
          {
            breakpoint: 1025,
            settings: {
              arrows: true,
              slidesToShow: 4,
              slidesToScroll: 4
            }
          },
          {
            breakpoint: 769,
            settings: {
              arrows: false,
              slidesToShow: 3,
              slidesToScroll: 3
            }
          },
          {
            breakpoint: 481,
            settings: {
              arrows: false,
              slidesToShow: 2,
              slidesToScroll: 1
            }
          }
        ]
      }
      const slickParams = this.carouselParams.slickParams ? this.carouselParams.slickParams : defaultSlickParams

      this.$carousel.slick(slickParams)
    } else {
      this.$carousel.slick({
        dots: false,
        arrows: false,
        swipe: false,
        touchMove: false,
        rows: 0, // https://github.com/kenwheeler/slick/issues/3149
        infinite: false,
        slidesToShow: this.forceCount,
        slidesToScroll: this.forceCount
      })
    }
  }

  fetchData () {
    if (this.isFetching) {
      return
    }
    this.isFetching = true

    this.carouselParams.dataPromise()
      .then((data) => {
        this.isFetching = false

        if (data) {
          if (!data.hasOwnProperty('results') || !data.hasOwnProperty('hasMore')) {
            console.error('ProductCarousel', 'carouselParams.dataPromise must return an object {results: [array], hasMore: [boolean]}')
            return
          }
          if (!data.results) {
            return
          }
          const pageSize = this.$carousel.slick('slickGetOption', 'slidesToShow')

          data.results.forEach((product) => {
            let $newCard = null
            let index = null

            // first, find any existing placeholders
            // could optimize this by avoiding the jquery lookups
            const $placeholders = this.slickObject.$slides.find('.productCard-placeholder').closest('.slick-slide')

            if ($placeholders.length) {
              $newCard = $placeholders.first()
              index = parseInt($newCard.attr('data-slick-index'))

              // unfortunately we need to remove, and then re-add the new card in order for slick to properly update the cloned slides
              this.$carousel.slick('slickRemove', index)
            } else {
              if ((this.forceCount > 0) && (this.slickObject.slideCount >= this.forceCount)) {
                return
              }

              $newCard = this.$cardTemplate.clone()
            }

            $newCard = this.renderCard($newCard, product)

            if (index >= this.slickObject.slideCount) {
              index = null
            }
            const addBefore = index !== null
            this.$carousel.slick('slickAdd', $newCard, index, addBefore)
          })

          // fill out the last remaining slide page with placeholders,
          // unless the recco told us there's nothing more
          if (data.hasMore) {
            const pageCardCount = this.slickObject.slideCount % pageSize
            const placeholdersNeeded = (pageCardCount === 0) ? pageSize : (pageSize - pageCardCount)

            for (let i = 0, len = placeholdersNeeded; i < len; i++) {
              // fill out remaining page
              this.$carousel.slick('slickAdd', this.getPlaceholder())
            }
          } else {
            // remove any placeholder stragglers
            const $leftoverPlaceholders = this.slickObject.$slides.find('.productCard-placeholder').closest('.slick-slide')
            if ($leftoverPlaceholders.length) {
              const slickIndex = $leftoverPlaceholders.first().attr('data-slick-index')
              for (let i = 0, len = $leftoverPlaceholders.length; i < len; i++) {
                this.$carousel.slick('slickRemove', slickIndex)
              }
            }
          }

          // picturefill for ie11
          if (window.picturefill) {
            window.picturefill({
              reevaluate: true,
              elements: this.$carousel.find('.slick-active img').get()
            })
          }
        }
      })
  }

  checkNeedData () {
    // check if the page has placeholders
    const currentSlide = this.$carousel.slick('slickCurrentSlide')
    const pageSize = this.$carousel.slick('slickGetOption', 'slidesToShow')

    const $pageSlides = this.slickObject.$slides.slice(currentSlide, currentSlide + pageSize)

    return ($pageSlides.find('.productCard-placeholder').length !== 0)
  }

  getPlaceholder () {
    const $newCard = this.$cardTemplate.clone()
    return $newCard
  }

  renderCard ($card, product) {
    const $productCard = $card.find('.productCard')

    // recreate the layout logic as found in _product-card.liquid, and _product-price.liquid
    const {
      title,
      handle,
      featured_image: featuredImage,
      price,
      compare_at_price: compareAtPrice
    } = product

    const $regularPrice = $productCard.find('[data-regular-price]')
    const $salePrice = $productCard.find('[data-price]')

    let currentPrice = parseFloat(price)
    let strikeoutPrice = compareAtPrice ? parseFloat(compareAtPrice) : 0.0

    $productCard.find('.productCard-title span').text(title)
    $productCard.find('.productCard-linkWrapper').attr('href', `/products/${handle}`)

    if (featuredImage) {
      const $sourceTag = $('<source srcset="" sizes="100" />')
      const imageSrcs = [
        formatImageUrl(featuredImage, 'x200'),
        formatImageUrl(featuredImage, 'x133')
      ]

      $sourceTag.attr('srcset', imageSrcs.join(','))
      $productCard.find('.productCard-featuredImage').prepend($sourceTag)
    }

    const productDiscount = getCustomerDiscount(product)
    if (productDiscount !== false) {
      if (strikeoutPrice === 0) {
        strikeoutPrice = currentPrice
      }
      currentPrice = productDiscount.finalPrice
    }

    if (strikeoutPrice) {
      $salePrice.html(formatMoney(currentPrice))
      $salePrice.removeClass('u--hide')

      $regularPrice.html(formatMoney(strikeoutPrice, true))
      $regularPrice.addClass('productPrice-price-strikeout')
    } else {
      $salePrice.remove()
      $productCard.find('.productPrice-salePriceText').remove()

      $regularPrice.html(formatMoney(currentPrice))
    }

    $productCard.removeClass('productCard-placeholder')

    return $card
  }
}

registerClass('ProductCarousel', ProductCarousel)
